import { useEffect, useState } from "react";
import useAuthStore from "../../store/authStore";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import AdminLayout from "../../layouts/AdminLayout";
import { getTeamTimesheet } from "../../api/employeeService";

// ── helpers ───────────────────────────────────────────────────────────────────
const getMonday = (d = new Date()) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(new Date(d).setDate(diff));
    mon.setHours(0, 0, 0, 0);
    return mon;
};

const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

const fmtISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
};

// All 7 day labels Mon→Sun
const ALL_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Format decimal hours → HH:MM
const toHHMM = (decimal) => {
    if (!decimal || decimal <= 0) return null;
    const h = Math.floor(decimal);
    const m = Math.round((decimal - h) * 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Color pill based on total hours logged that day
const hrsPillColor = (decimal) => {
    if (!decimal || decimal <= 0) return null;
    if (decimal >= 9)  return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
    if (decimal >= 8)  return "bg-yellow-500/20 text-yellow-300 border-yellow-500/30";
    return "bg-orange-500/20 text-orange-300 border-orange-500/30";
};

const displayVal = (v) => (!v || String(v).trim() === "" ? "—" : v);

export default function TeamReview() {
    const navigate = useNavigate();
    const [monday] = useState(() => getMonday());
    const loggedRole = useAuthStore((state) => state.role) || 'MANAGER';
    const Layout = loggedRole === "ADMIN" ? AdminLayout : ManagerLayout;

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedEmp, setExpandedEmp] = useState(null);
    const [showSunday, setShowSunday] = useState(true);

    const [selectedRole, setSelectedRole] = useState(() => {
        return sessionStorage.getItem("team_review_role") || "MANAGER";
    });
    const [selectedManagerId, setSelectedManagerId] = useState(() => {
        return sessionStorage.getItem("team_review_manager_id") || "";
    });
    const [managersList, setManagersList] = useState([]);

    useEffect(() => {
        sessionStorage.setItem("team_review_role", selectedRole);
    }, [selectedRole]);

    useEffect(() => {
        if (selectedManagerId) {
            sessionStorage.setItem("team_review_manager_id", selectedManagerId);
        }
    }, [selectedManagerId]);

    // Fetch all managers for dropdown selection if logged role is ADMIN
    useEffect(() => {
        if (loggedRole === "ADMIN") {
            getTeamTimesheet({ week_start: fmtISO(monday), role: "MANAGER" })
                .then((res) => {
                    const list = res.data.employees || [];
                    setManagersList(list);
                    const savedManagerId = sessionStorage.getItem("team_review_manager_id");
                    if (savedManagerId && list.some(m => String(m.employee_id) === String(savedManagerId))) {
                        setSelectedManagerId(savedManagerId);
                    } else if (list.length > 0) {
                        setSelectedManagerId(list[0].employee_id);
                    }
                })
                .catch((err) => {
                    console.error("Failed to load managers list:", err);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── fetch team timesheet data (current week only) ── */
    useEffect(() => {
        setLoading(true);
        const params = { week_start: fmtISO(monday) };
        if (loggedRole === "ADMIN") {
            params.role = selectedRole;
            if (selectedRole === "EMPLOYEE" && selectedManagerId) {
                params.manager_id = selectedManagerId;
            }
        }
        getTeamTimesheet(params)
            .then((res) => {
                const data = res.data.employees || [];
                const loggedUserId = useAuthStore.getState().employeeId;
                const filtered = loggedUserId
                    ? data.filter(emp => String(emp.employee_id) !== String(loggedUserId))
                    : data;
                setEmployees(filtered);
                setShowSunday(res.data.show_sunday !== false);
            })
            .catch((err) => {
                console.error("Failed to load team timesheet data:", err);
                setEmployees([]);
                setShowSunday(true);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole, selectedManagerId]);

    const toggleExpand = (empId) =>
        setExpandedEmp(prev => (prev === empId ? null : empId));

    // Sum hours for a given date across all employees
    const getDayTotal = (assignments, isoDate) =>
        assignments
            .filter(a => a.activity_date === isoDate)
            .reduce((sum, a) => sum + (a.duration_hours || 0), 0);

    // Final visible columns (indices into allWeekDates)
    const allWeekDates = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    const visibleDates = showSunday ? allWeekDates : allWeekDates.slice(0, 6);

    return (
        <Layout>
            <div className="p-3 sm:p-6 max-w-[1400px] mx-auto">

                {/* ── Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6 mb-4 sm:mb-6"
                >
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                            <span className="text-2xl">👥</span>
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-white">Weekly Team Review</h1>
                            <p className="text-white/60 text-xs sm:text-sm">
                                {loggedRole === "ADMIN"
                                    ? selectedRole === "MANAGER"
                                        ? "Timesheet entries (Project · Discipline · Stage · Duration) for managers"
                                        : "Timesheet entries (Project · Discipline · Stage · Duration) for employees"
                                    : "Timesheet entries (Project · Discipline · Stage · Duration) for your reporting team"
                                }
                            </p>
                        </div>
                    </div>

                    {loggedRole === "ADMIN" && (
                        <div className="mt-4 flex flex-wrap gap-4 items-center">
                            <div className="flex items-center gap-2">
                                <label className="text-white/70 text-xs font-semibold">Select Role:</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    style={{ colorScheme: "dark" }}
                                    className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl px-3 py-1.5 text-white text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:border-indigo-400 cursor-pointer shadow"
                                >
                                    <option value="MANAGER" className="bg-gray-900 text-white">Manager</option>
                                    <option value="EMPLOYEE" className="bg-gray-900 text-white">Employee</option>
                                </select>
                            </div>

                            {selectedRole === "EMPLOYEE" && (
                                <div className="flex items-center gap-2">
                                    <label className="text-white/70 text-xs font-semibold">Select Manager:</label>
                                    <select
                                        value={selectedManagerId}
                                        onChange={(e) => setSelectedManagerId(e.target.value)}
                                        style={{ colorScheme: "dark" }}
                                        className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl px-3 py-1.5 text-white text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:border-indigo-400 cursor-pointer shadow"
                                    >
                                        <option value="" disabled className="bg-gray-900 text-white/50">Select Manager...</option>
                                        {managersList.map((mgr) => (
                                            <option key={mgr.employee_id} value={mgr.employee_id} className="bg-gray-900 text-white">
                                                {mgr.full_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Legend */}
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/60">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span> ≥ 9:00 hrs</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-300 inline-block"></span> 8:00 – 8:59 hrs</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block"></span> &lt; 8:00 hrs</span>
                        <span className="flex items-center gap-1"><span className="text-white/30">—</span>&nbsp; No entries</span>
                        <span className="ml-auto text-white/40 italic">Click a row to view daily details</span>
                    </div>
                </motion.div>

                {/* ── Summary Table (desktop) ── */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="hidden lg:block bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl"
                >
                    {loading ? (
                        <div className="p-10 text-center text-white/60">
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                                Loading timesheet data...
                            </div>
                        </div>
                    ) : employees.length === 0 ? (
                        <div className="p-10 text-center text-white/40">
                            No team members found for this week.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-white" style={{ tableLayout: "fixed" }}>
                                <colgroup>
                                    <col style={{ width: "100px" }} />
                                    <col style={{ width: "160px" }} />
                                    <col style={{ width: "80px" }} />
                                    {visibleDates.map((_, i) => (
                                        <col key={i} style={{ width: "100px" }} />
                                    ))}
                                </colgroup>

                                <thead className="bg-white/20 border-b border-white/30">
                                    <tr>
                                        <th className="p-3 text-left pl-5 text-xs font-semibold text-white/80 tracking-wide">Emp ID</th>
                                        <th className="p-3 text-left text-xs font-semibold text-white/80 tracking-wide">Employee Name</th>
                                        <th className="p-3 text-center text-xs font-semibold text-white/80 tracking-wide">Role</th>
                                        {visibleDates.map((d, i) => {
                                            const isSun = d.getDay() === 0;
                                            return (
                                                <th key={i} className={`p-3 text-center text-xs font-semibold tracking-wide ${isSun ? "text-orange-300/80" : "text-white/80"}`}>
                                                    {ALL_DAY_LABELS[i === 6 && showSunday ? 6 : i]}{" "}
                                                    <span className="text-white/50">{String(d.getDate()).padStart(2, "0")}</span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {employees.map((emp, idx) => {
                                        const isExpanded = expandedEmp === emp.employee_id;
                                        const totalCols = 3 + visibleDates.length;
                                        const targetPath = emp.role === "MANAGER"
                                            ? `/manager/activity?employee_id=${emp.employee_id}${loggedRole === "ADMIN" && selectedManagerId ? `&manager_id=${selectedManagerId}` : ""}`
                                            : `/employee/assigned-projects?employee_id=${emp.employee_id}${loggedRole === "ADMIN" && selectedManagerId ? `&manager_id=${selectedManagerId}` : ""}`;
                                        return (
                                            <>
                                                {/* ── Summary row ── */}
                                                <tr
                                                    key={`row-${emp.employee_id}`}
                                                    onClick={() => toggleExpand(emp.employee_id)}
                                                    className={`border-b border-white/10 transition cursor-pointer hover:bg-white/10 ${idx % 2 === 0 ? "bg-white/5" : "bg-transparent"} ${isExpanded ? "bg-indigo-500/10 border-indigo-500/30" : ""}`}
                                                >
                                                    {/* Emp ID */}
                                                    <td className="p-3 pl-5">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(targetPath); }}
                                                            className="text-cyan-400 font-bold hover:text-cyan-300 hover:underline transition text-sm"
                                                        >
                                                            {emp.employee_id}
                                                        </button>
                                                    </td>

                                                    {/* Name */}
                                                    <td className="p-3">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className={`transition-transform text-white/40 text-xs ${isExpanded ? "rotate-90" : ""}`}>▶</span>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); navigate(targetPath); }}
                                                                className="text-blue-300 font-semibold hover:text-blue-200 hover:underline transition text-sm text-left"
                                                            >
                                                                {emp.full_name}
                                                            </button>
                                                        </div>
                                                    </td>

                                                    {/* Role */}
                                                    <td className="p-3 text-center">
                                                        <span className="px-2 py-0.5 rounded-lg bg-white/10 border border-white/20 text-white/70 text-xs">
                                                            {emp.role}
                                                        </span>
                                                    </td>

                                                    {/* Per-day totals */}
                                                    {visibleDates.map((d, i) => {
                                                        const iso = fmtISO(d);
                                                        const dayTotal = getDayTotal(emp.assignments, iso);
                                                        const hhmm = toHHMM(dayTotal);
                                                        const pillColor = hrsPillColor(dayTotal);
                                                        return (
                                                            <td key={i} className="p-3 text-center">
                                                                {hhmm ? (
                                                                    <span className={`inline-block px-2 py-0.5 rounded-lg border text-xs font-semibold ${pillColor}`}>
                                                                        {hhmm}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-white/30 text-sm">—</span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>

                                                {/* ── Expanded detail rows ── */}
                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <tr key={`expand-${emp.employee_id}`}>
                                                            <td colSpan={totalCols} className="p-0 bg-black/20">
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: "auto", opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    transition={{ duration: 0.2 }}
                                                                    className="overflow-hidden"
                                                                >
                                                                    <div className="px-4 py-3">
                                                                        {emp.assignments.length === 0 ? (
                                                                            <p className="text-white/40 text-sm text-center py-2">
                                                                                No timesheet entries for this week.
                                                                            </p>
                                                                        ) : (
                                                                            <table className="w-full text-xs border-collapse">
                                                                                <thead>
                                                                                    <tr className="bg-white/10 border-b border-white/20 text-white/70 text-[11px] uppercase tracking-wider">
                                                                                        <th className="py-2 px-3 text-left">Date</th>
                                                                                        <th className="py-2 px-3 text-left">Project</th>
                                                                                        <th className="py-2 px-3 text-left">Discipline</th>
                                                                                        <th className="py-2 px-3 text-left">Stage</th>
                                                                                        <th className="py-2 px-3 text-left">Description</th>
                                                                                        <th className="py-2 px-3 text-center">Duration</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {emp.assignments.map((a, aidx) => (
                                                                                        <tr key={a.id} className={`border-b border-white/10 ${aidx % 2 === 0 ? "bg-white/5" : ""}`}>
                                                                                            <td className="py-2 px-3 text-white/60 whitespace-nowrap">{a.activity_date}</td>
                                                                                            <td className="py-2 px-3 text-white">{displayVal(a.project_name)}</td>
                                                                                            <td className="py-2 px-3 text-white">{displayVal(a.discipline_name)}</td>
                                                                                            <td className="py-2 px-3 text-white">{displayVal(a.stage_name)}</td>
                                                                                            <td className="py-2 px-3 text-white/70 max-w-[200px] truncate">{displayVal(a.description)}</td>
                                                                                            <td className="py-2 px-3 text-center">
                                                                                                <span className="font-mono text-indigo-300 font-semibold">
                                                                                                    {toHHMM(a.duration_hours) || "—"}
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        )}
                                                                    </div>
                                                                </motion.div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </AnimatePresence>
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </motion.div>

                {/* ── Mobile / Tablet Cards ── */}
                <div className="lg:hidden mt-4 space-y-3">
                    {loading ? (
                        <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center text-white/60">
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                                Loading timesheet data...
                            </div>
                        </div>
                    ) : employees.length === 0 ? (
                        <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center text-white/60">
                            No team members found for this week.
                        </div>
                    ) : (
                        employees.map((emp, i) => {
                            const isExpanded = expandedEmp === emp.employee_id;
                            const targetPath = emp.role === "MANAGER"
                                ? `/manager/activity?employee_id=${emp.employee_id}${loggedRole === "ADMIN" && selectedManagerId ? `&manager_id=${selectedManagerId}` : ""}`
                                : `/employee/assigned-projects?employee_id=${emp.employee_id}${loggedRole === "ADMIN" && selectedManagerId ? `&manager_id=${selectedManagerId}` : ""}`;
                            return (
                                <motion.div
                                    key={emp.employee_id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.04 }}
                                    className="bg-white/10 border border-white/20 rounded-2xl overflow-hidden shadow-lg"
                                >
                                    {/* Card header */}
                                    <div
                                        onClick={() => toggleExpand(emp.employee_id)}
                                        className="flex items-center justify-between px-4 py-3 border-b border-white/15 bg-white/10 cursor-pointer hover:bg-white/15 transition"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`text-white/40 text-xs transition-transform ${isExpanded ? "rotate-90" : ""}`}>▶</span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(targetPath); }}
                                                className="text-cyan-400 font-bold text-sm hover:underline"
                                            >
                                                {emp.employee_id}
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(targetPath); }}
                                                className="text-blue-300 font-semibold text-sm hover:underline"
                                            >
                                                {emp.full_name}
                                            </button>
                                        </div>
                                        <span className="px-2 py-0.5 rounded-lg bg-white/10 border border-white/20 text-white/70 text-xs">
                                            {emp.role}
                                        </span>
                                    </div>

                                    {/* Day summary grid — show 6 or 7 days based on Sunday data */}
                                    <div className={`p-3 grid gap-2 ${visibleDates.length === 7 ? "grid-cols-4 sm:grid-cols-7" : "grid-cols-3 sm:grid-cols-6"}`}>
                                        {visibleDates.map((d, di) => {
                                            const iso = fmtISO(d);
                                            const dayTotal = getDayTotal(emp.assignments, iso);
                                            const hhmm = toHHMM(dayTotal);
                                            const isSun = d.getDay() === 0;
                                            return (
                                                <div key={di} className={`rounded-xl p-2 text-center ${isSun ? "bg-orange-500/10 border border-orange-500/20" : "bg-white/10"}`}>
                                                    <p className={`text-[10px] mb-1 ${isSun ? "text-orange-300/70" : "text-white/50"}`}>
                                                        {ALL_DAY_LABELS[di === 6 && showSunday ? 6 : di]} {String(d.getDate()).padStart(2, "0")}
                                                    </p>
                                                    {hhmm ? (
                                                        <p className={`font-bold text-xs ${dayTotal >= 9 ? "text-emerald-300" : dayTotal >= 8 ? "text-yellow-300" : "text-orange-300"}`}>
                                                            {hhmm}
                                                        </p>
                                                    ) : (
                                                        <p className="text-white/30 text-xs">—</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Expanded detail */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden border-t border-white/15"
                                            >
                                                <div className="p-3 space-y-2">
                                                    {emp.assignments.length === 0 ? (
                                                        <p className="text-white/40 text-sm text-center py-2">No entries this week.</p>
                                                    ) : (
                                                        emp.assignments.map((a) => (
                                                            <div key={a.id} className="bg-white/10 rounded-xl p-3 space-y-1">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-white/50 text-xs">{a.activity_date}</span>
                                                                    <span className="font-mono text-indigo-300 font-bold text-xs">{toHHMM(a.duration_hours) || "—"}</span>
                                                                </div>
                                                                <p className="text-white text-xs font-semibold">{displayVal(a.project_name)}</p>
                                                                <div className="flex gap-2 flex-wrap">
                                                                    {a.discipline_name && <span className="text-white/60 text-[10px] bg-white/10 rounded px-1.5 py-0.5">{a.discipline_name}</span>}
                                                                    {a.stage_name && <span className="text-white/60 text-[10px] bg-white/10 rounded px-1.5 py-0.5">{a.stage_name}</span>}
                                                                </div>
                                                                {a.description && <p className="text-white/50 text-[10px]">{a.description}</p>}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })
                    )}
                </div>

            </div>
        </Layout>
    );
}
